/* eslint-disable max-lines */
import { track } from "jiffy-analytics";
import { isTouchDevice } from "common/utils/browser";
import { on as onEvent } from "common/utils/ui/event";
import useSvg from "common/utils/ui/use_svg";
import "../softness_score_compact/component";
import "./component.scss";

export const urlEnum = {
  print_compositions: "print_compositions"
};

const CLASS_NAMES = {
  JS_SCORE_TOOLTIP: ".js-softness-score-tooltip",
  JS_SCORE_NUMBER: ".js-softness-score-number",
  SOFTNESS_SCORE: ".softness-score",
  SOFTNESS_SCORE_MINI: ".softness-score-mini__number",
  LEARN_MORE: ".softness-score__learn-more"
};

const DPD_DYNAMIC_SOURCES = ["product_details_user_feedback", "product_color_picker"];

const softnessLevels = [
  { label: "Very soft", text: "Ultra-luxe softness" },
  { label: "Soft", text: "Smooth and comfy" },
  { label: "Everyday", text: "Basic comfort" },
  { label: "Rough", text: "Coarse feel" }
];

const tooltipContent = () => {
  const softnessItems = softnessLevels
    .map(
      ({ label, text }) => `
    <div class="flex items-center">
      <div class="w-20 h-6 bg-blue-700 rounded-2xl flex items-center justify-center mr-2">
        <span class="text-white text-sm font-semibold">${label}</span>
      </div>
      <span class="text-gray-600 text-sm">${text}</span>
    </div>
  `
    )
    .join("");

  return `
    <div
      class="w-[274px] bg-white border border-blue-700 rounded-lg shadow-lg"
      id="js-softness-score-tooltip-content"
    >
      <div class="bg-blue-700 rounded-t-lg px-[10.89px] py-[11px] flex items-center justify-between h-10">
        <div class="flex items-center">
          <div class="w-[16.33px] h-[18px] mr-[9px] js-icon-placeholder"></div>
          <h5 class="text-white text-sm font-bold leading-5">
            Softness Score
          </h5>
        </div>
        <a
          class="w-[16.33px] h-[18px] text-white js-softness-score-tooltip-close"
          role="button"
          tabindex="-1"
        ></a>
      </div>
      <div class="p-2">
        <div class="space-y-1">
          ${softnessItems}
        </div>
      </div>
    </div>
  `;
};

const tooltipTemplate = `
  <div class="tooltip softness-tooltip" role="tooltip">
    <div class="tooltip-inner p-0 bg-transparent border-0 rounded-lg max-w-none"></div>
  </div>`;

const $getTooltips = () => document.querySelectorAll(".js-softness-score-tooltip");
const $$getTooltips = () => $(".js-softness-score-tooltip");

/*  Bootstrap tooltips to stay open on hover https://stackoverflow.com/a/36056707 */
export const initStayOpenOnHover = () => {
  const originalLeave = $.fn.tooltip.Constructor.prototype.leave;

  $.fn.tooltip.Constructor.prototype.leave = function leave(event) {
    const { currentTarget } = event;
    const options = this.getDelegateOptions();
    const dynamicDataValue = $(currentTarget)[this.type](options).data(`bs.${this.type}`);
    const self = event instanceof this.constructor ? event : dynamicDataValue;

    originalLeave.call(this, event);

    if (currentTarget) {
      const $$container = $(`#${$(currentTarget).attr("aria-describedby")}`);
      const hasLink = !!$("a[href]", $$container).length;
      if (!hasLink) return;

      const { timeout } = self;
      $$container.one("mouseenter", () => {
        clearTimeout(timeout);

        $$container.one("mouseleave", () => {
          $.fn.tooltip.Constructor.prototype.leave.call(self, self);
        });
      });
    }
  };
};

const $getSoftnessTooltip = $element => {
  const $tooltip = $element
    .closest(CLASS_NAMES.JS_SCORE_TOOLTIP)
    ?.querySelector(CLASS_NAMES.JS_SCORE_NUMBER);

  if ($tooltip) {
    return $tooltip;
  }

  return (
    $element
      .closest(CLASS_NAMES.JS_SCORE_TOOLTIP)
      ?.querySelector(CLASS_NAMES.SOFTNESS_SCORE_MINI) || null
  );
};

const getPageType = () => {
  const currentUrl = window.location.href.replace(/\/+$/, ""); // Remove trailing slashes

  if (currentUrl.includes(".html")) {
    return "detail_page";
  }
  if (currentUrl === window.location.origin || currentUrl === `${window.location.origin}/`) {
    return "home_page";
  }
  return "listing_page";
};

let isLearnMoreOnEventAttached = false;
let activeSoftnessPayload = {};
const initTrackLearnMoreClick = () => {
  if (isLearnMoreOnEventAttached) return;

  const callback = _ => {
    track("softness_score_learn_more_clicked", activeSoftnessPayload);
  };

  onEvent("click", CLASS_NAMES.LEARN_MORE, callback, true);
  isLearnMoreOnEventAttached = true;
};

const trackSoftnessScoreBadgeOpened = $element => {
  const $softnessTooltip = $getSoftnessTooltip($element);
  const score = $softnessTooltip?.textContent.trim() || "";

  const dataset = $softnessTooltip?.dataset;
  const page = dataset?.source?.includes("page") ? dataset.source : getPageType();
  const source = dataset?.source || "left_nav";
  let compositionName = dataset?.compositionName || "";

  if (page === "detail_page" && DPD_DYNAMIC_SOURCES.includes(source)) {
    const urlParams = new URLSearchParams(window.location.search);
    compositionName = urlParams.get("ac") || compositionName;
  }

  const softnessPayload = {
    score,
    source,
    composition_name: compositionName,
    catalog_number: dataset?.catalogNumber || "",
    page
  };

  track("softness_score_badge_opened", softnessPayload);
  activeSoftnessPayload = softnessPayload;
};

const setupTouchTooltips = $$tooltips => {
  $$tooltips.each(function attachClickHandler() {
    const element = this;
    $(element).on("click", e => {
      e.preventDefault();
      e.stopPropagation();

      const $element = $(element);
      const isShown = $element.attr("aria-describedby");

      // Hide all other tooltips
      $$tooltips.not(element).tooltip("hide");

      // Toggle current tooltip
      $element.tooltip(isShown ? "hide" : "show");
    });
  });
};

const initTooltip = () => {
  const $$tooltips = $$getTooltips();

  // Unbind any existing event handlers to prevent duplicate bindings
  $$tooltips.off("shown.bs.tooltip");

  $$tooltips
    .tooltip({
      animation: false,
      container: "body",
      delay: {
        show: isTouchDevice() ? 0 : 500,
        hide: 100
      },
      html: true,
      sanitize: false, // Disable HTML sanitization to allow SVG elements
      title: tooltipContent(),
      template: tooltipTemplate,
      trigger: isTouchDevice() ? "manual" : "hover",
      placement: "top" // Place tooltip at the top instead of right
    })
    .on("shown.bs.tooltip", function shownCallback() {
      $$tooltips.not(this).tooltip("hide");

      trackSoftnessScoreBadgeOpened(this);

      // Inject SVG icon after tooltip is shown
      const iconPlaceholder = document.querySelector(".js-icon-placeholder");
      if (iconPlaceholder) {
        iconPlaceholder.innerHTML = useSvg("softness-clear", {
          class: "w-[16.33px] h-[18px] text-white"
        });
        iconPlaceholder.classList.remove("js-icon-placeholder");
      }
    });

  // For touch devices, manually handle tooltip show/hide
  if (isTouchDevice()) {
    setupTouchTooltips($$tooltips);
  }
};

const initTooltipClose = () => {
  const callback = e => {
    e.preventDefault();
    $$getTooltips().tooltip("hide");
  };

  onEvent("click", ".js-softness-score-tooltip-close", callback, true);

  // On touch devices, close tooltip when clicking outside
  if (isTouchDevice()) {
    document.addEventListener(
      "click",
      e => {
        // Check if click is outside tooltip and trigger element
        const isTooltipClick = e.target.closest(".tooltip");
        const isTriggerClick = e.target.closest(".js-softness-score-tooltip");

        if (!isTooltipClick && !isTriggerClick) {
          $$getTooltips().tooltip("hide");
        }
      },
      true
    );
  }
};

const stopBubbling = () => {
  $getTooltips().forEach($badge => {
    $badge.addEventListener("click", e => {
      e.preventDefault();
      e.stopPropagation();
    });
  });
};

const init = () => {
  const currUrl = window.location.href;

  const isPrintCompositionsPage = currUrl.includes(urlEnum.print_compositions);

  initStayOpenOnHover();

  if (!isPrintCompositionsPage) {
    initTooltip();
    initTrackLearnMoreClick();
  }

  initTooltipClose();
  stopBubbling();
};

export default init;
